import { Optional, Component, OnInit, AfterViewInit, OnChanges, OnDestroy } from '@angular/core';
import { Input, forwardRef } from '@angular/core';
import { Output, EventEmitter } from '@angular/core';
import { ViewChild, HostBinding } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { ControlContainer, ControlValueAccessor }  from "@angular/forms";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

import { Subscription } from 'rxjs';

import { platformService } from '@unpispas/upp-base';

// refs: https://almerosteyn.com/2016/04/linkup-custom-control-to-ngcontrol-ngmodel

interface ErrorInfo {
    [key: string]: string;
}

/** Preserves numeric **0** from reactive forms (`value || null` would drop zero). */
function stringifyFormBoundValue(raw: unknown): string | null {
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return String(raw);
}

/**********************************/
/* UPP-INPUT KEYBOARD COMPONENT   */
/**********************************/

/**
 * A custom Angular component for keyboard-based input.
 * Supports integration with Angular Forms through `ControlValueAccessor`.
 * Provides custom behavior for `price` and `number` types, and supports kiosk mode.
 *
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
    selector: 'upp-kb-input',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./kb/upp-input.html',
    styleUrls: [ 
        './kb/upp-input.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppKbInputComponent),
            multi: true
        }            
    ]
})
export class UppKbInputComponent implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy {
    /** Placeholder text for the input. */
    @Input() placeholder = '';
    /** Indicates if the input should be readonly. */
    @Input() readonly = false;
    /** Name of the input control. */
    @Input() name = 'none';
    /** Enables or disables kiosk mode for the input. */
    @Input() kiosk = true;
    /** Type of the input (e.g., `text`, `number`, `price`). */
    @Input() type = '';
    /** Disables the input when set to true. */
    @Input() disabled = false;
    /** Configures autocomplete behavior for the input. */
    @Input() autocomplete = 'on';

    /** Emits the current value when it changes. */
    @Output() Changed = new EventEmitter<string | null>();
    /** Emits an event when the input gains focus. */
    @Output() Focused = new EventEmitter<void>(); 

    /** Reference to the native input element. */
    @ViewChild('input', {static: false}) _input: any;

    /** Internal flag indicating if the input is disabled. */
    public isDisabled = false;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback = (_v:any) => {
        // nothing to do (required by angular)
    };

    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    };
    /* eslint-enable @typescript-eslint/no-unused-vars */

    /** Internal value of the input. */
    private _innerValue: string | null | undefined = undefined;  

    /** Indicates if the component is in kiosk mode. */
    public _kiosk = false;
    /** Tracks if the input currently has focus. */
    public _focus = false;

    /**
     * Handles focus events and emits `uppFocus`.
     * @param value Whether the input has focus.
     */    
    onFocus(value: boolean): void{
        this._focus = value;
        this.Focused.emit();
    }

    /**
     * Native element reference for the input.
     * @returns {any} The parent node of the input element.
     */    
    get nativeElement(): any{
        if (!this._input){
            return null;
        }

        return this._input?.nativeElement.parentNode;
    }

    /**
     * Simulates a key press for the input element.
     * @param key The key to simulate.
     */    
    OnKeyPress(key: string): void{
        const _event = new KeyboardEvent("keyup", {
            key: key,
            shiftKey: false, // you don't need to include values
            ctrlKey: false,  // if you aren't going to use them.
            metaKey: false   // these are here for example's sake.
        })        

        this._onKeyUp(_event);
    }

    /**
     * Sets focus on the input element.
     */    
    SetFocus(): void{
        if (!this._input){
            return;
        }

        setTimeout(() => {
            this._input.nativeElement.focus();
            this._input.nativeElement.click();
        }, 50);
    }

    /**
     * Gets the formatted price value.
     * @returns {string} The formatted price or placeholder text.
     */    
    get Price(): string{
        if (!this._innerValue) {
            return this._focus ? '' : this.placeholder;
        }
        
        return Number(this._innerValue).toFixed(2);
    }

    /**
     * Constructs an instance of `UppKbInputComponent`.
     *
     * @param {ChangeDetectorRef} change - A service to trigger manual change detection, ensuring the component reflects updates to its state.
     * @param {platformService} platform - A service to provide platform-specific functionality, such as kiosk detection.
     */    
    constructor(private change: ChangeDetectorRef, private platform: platformService) {
        // nothing to do
    }

    private _kiosk_subscription: Subscription | null = null;

    /**
     * Lifecycle hook. Called after the view is initialized.
     * Configures kiosk mode and subscribes to platform changes.
     */
    ngAfterViewInit(){
        this.platform.ready().
        then(() => {
            this._kiosk = this.kiosk && this.platform._isKiosk;
            this.change.markForCheck();
             
            if (this.kiosk){    // do not subscribe if kiosk is disabled
                this._kiosk_subscription = this.platform.OnKioskChanged.subscribe(
                () => {
                    this._kiosk = this.platform._isKiosk;
                    this.change.markForCheck();                
                });
            }
        });
    }

    public _price = '';     // keep the complete price value

    /**
     * Lifecycle hook. Updates the component when inputs change.
     */    
    ngOnChanges() {
        if (this.type == 'price'){
            const _parts = this.value ? this.value.toString().split('.') : [ '0', '0' ];
            if (_parts.length == 2){
                this._price = _parts[0] + (_parts[1] + "00").slice(0, 2); 
            }
        }
    }

    /**
     * Lifecycle hook. Cleans up subscriptions on destroy.
     */    
    ngOnDestroy(){
        if (this._kiosk_subscription){
            this._kiosk_subscription.unsubscribe();
            this._kiosk_subscription = null;
        }       
    }

    /**
     * Determines the type of the input field.
     * Returns `text` if the input type is `password` and the password visibility is toggled on.
     * Otherwise, it returns the original input type.
     * 
     * @returns {string} The type of the input (`text` or `password`).
     */    
    get _text(): string{
        if (this.type == 'password'){
            return (this._showpassword) ? 'text' : 'password';
        }

        return 'text';
    }

    private _showpassword = false;
    
    /**
     * Indicates whether the password input is visible (`true`) or hidden (`false`).
     * 
     * @returns {boolean} The visibility state of the password input field.
     */    
    get ShowPassword(){
        return this._showpassword;
    }

    /**
     * Toggles the visibility of the password input field.
     * Changes the input type between `text` and `password`.
     * Triggers Angular's change detection to update the UI state.
     * 
     * @returns {void}
     */    
    OnShowPassword(){
        this._showpassword = !this._showpassword;
        this.change.markForCheck();
    }

    /**
     * Handles the `change` event for input fields, specifically designed to capture browser autocomplete changes.
     * Unlike `onInput` and `onKeyUp`, this method will trigger when the browser's autocomplete 
     * feature populates the input field, ensuring that autocompleted values are properly processed.
     * 
     * The method processes the input value according to the component's type:
     * - For regular text inputs, it simply updates the value
     * - For price inputs, it converts the input to the correct price format
     * - For number inputs, it handles decimal separators appropriately
     * 
     * @param {Event} event - The DOM change event from the input element
     * @returns {void}
     */    
    onChange(event: Event): void {
        const _value = event.target ? (event.target as HTMLInputElement).value : '';
        if (_value != ''){
            this._innerValue = null;
        }

        switch(this.type){
            case 'price':
                this._price = _value;
                this.value = _value.replace(",", ".");
                break;

            case 'number':
                this.value = _value.replace(",", ".");
                break;

            default:
                this.value = _value;
                break;
        }
    }

    /**
     * Handles the `input` event for text and `number` fields (not `price`).
     * For `number`, the model must update here — before Angular CD re-applies
     * one-way `[value]` — so digit-by-digit typing does not lose intermediate keys.
     *
     * @param {Event} event - The `input` event triggered by the user.
     * @returns {void}
     */    
    onInput(event: Event): void {
        if (this.readonly){
            return;
        }

        const _value = event.target ? (event.target as HTMLInputElement).value : '';
        if (this.type === 'price') {
            return;
        }
        if (this.type === 'number') {
            this._CommitNumber(_value);
            return;
        }
        this.value = _value;
    }

    /**
     * Template keyup handler: `price` always; `number` for pads without a DOM target
     * (`OnKeyPress`). Physical `number` typing commits in `onInput` (see #65).
     */
    onKeyUp(event: KeyboardEvent): void {
        if ([ 'price', 'number' ].includes(this.type)) {
            this._onKeyUp(event);
        }
    }

    /**
     * Updates the input value from a key event (price/number parsing or pad-simulated text keys).
     * @param event The keyboard event.
     */
    private _onKeyUp(event: KeyboardEvent) : void {
        if (this.readonly){
            return;
        }

        switch(this.type){
            case 'price': {
                if (event.key == 'Backspace'){
                    this._price = this._price || '';
                    if (this._price.length > 0){
                        this._price = this._price.slice(0, -1);
                    }
                }
                else {
                    if (isNaN(Number(event.key))){
                        return;     // ignore key if not a number
                    }

                    this._price = (this._price || '') + event.key;
                }

                this.value = (Number(this._price)/100).toString();
                if (isNaN(Number(this.value))){
                    this.value = this._price = '';
                }
                break;
            }

            case 'number': {
                const target = event.target as HTMLInputElement | null;
                if (target) {
                    // Idempotent with onInput for physical keys; keeps keyup path safe.
                    this._CommitNumber(target.value ?? '');
                }
                else {
                    if (event.key == 'Backspace'){
                        this._price = this._price ? this._price.slice(0, -1) : '';
                    }
                    else {
                        if (isNaN(Number(event.key)) && ([',', '.'].indexOf(event.key) == -1)){
                            return;     // ignore key if not a number, ',' or '.'
                        }
        
                        this._price = (this._price || '') + event.key;    
                    }

                    this._CommitNumber(this._price);
                }
                
                break;
            }

            default: {
                if (event.key === 'Backspace'){
                    this.value = this.value ? this.value.slice(0, -1) : '';
                }
                else if (event.key && event.key.length === 1){
                    this.value = (this.value ?? '') + event.key;
                }
                break;
            }
        }
    }

    /**
     * Handles changes when in kiosk mode.
     * @param event The input event.
     */    
    onKiosk(event: Event): void{
        if (this.readonly){
            return;
        }

        const _value = event.target ? (event.target as HTMLInputElement).value : '';

        switch(this.type){
            case 'price':
                this.value = _value ? (Number(_value)/100).toString() : '';
                if (isNaN(Number(this.value))){
                    this.value = '';
                }
                break;

            case 'number':
                this.value = _value ? _value.replace(",", ".") : '';
                if (isNaN(Number(this.value))){
                    this.value = '';
                }
                break;

            default:
                this.value = _value ? _value : '';
                break;
        }
    }

    /********************************************/
    /* get/set accesors                         */
    /********************************************/

    @Input()

    /**
     * Gets the current value of the input.
     * This represents the internal state of the input field.
     * 
     * @returns {string | null} The current value of the input.
     */    
    get value(): string | null {
        if (this._innerValue === null || this._innerValue === undefined || this._innerValue === '') {
            return null;
        }
        return this._innerValue;
    }

    /**
     * Sets a new value for the input.
     * This updates the internal state, triggers the `onChange` and `onTouch` callbacks, 
     * and emits the `uppChanged` event.
     * 
     * @param {string | null} v - The new value to set.
     */    
    set value(v: string | null) {
        const _firstcall = (this._innerValue === undefined);

        if (v !== this._innerValue) {
            this._innerValue = v;
            
            if (v === null){
                this._price = '';
            }

            this._onChangeCallback(v);
            this._onTouchCallback();

            if (!_firstcall){
                this.Changed.emit(v);
            }
        }
    }    
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    /**
     * Writes a value to the component. Required by `ControlValueAccessor`.
     * @param value The new value to set.
     */    
    writeValue(value: unknown) {
        const asStr = stringifyFormBoundValue(value);
        if (asStr !== this._innerValue) {
            this.value = asStr;
            this.change.markForCheck();
        }
    }

    /**
     * Registers a callback function to be called when the value changes.
     * @param fn The callback function.
     */    
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Registers a callback function to be called when the input is touched.
     * @param fn The callback function.
     */    
    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    }  
    
    /**
     * Sets the disabled state of the input.
     * @param isDisabled Whether the input should be disabled.
     */    
    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    /********************************************/
    /* PRIVATE MEMBERS                          */
    /********************************************/

    /**
     * Normalizes and publishes a raw DOM string for `type === 'number'`
     * (comma → dot; clear if not numeric, allowing a trailing decimal point).
     */
    private _CommitNumber(raw: string): void {
        this._price = raw;

        const _value = raw.replace(",", ".");
        this.value = _value;

        const _isnumber = _value.endsWith('.') ? _value.slice(0, _value.length - 1) : _value;
        if (_value !== '' && isNaN(Number(_isnumber))){
            this.value = this._price = '';
        }
    }
}

/**********************************/
/* UPP-INPUT ERROR COMPONENT      */
/**********************************/

/**
 * A component for displaying validation errors for form controls.
 * Automatically integrates with Angular reactive forms using `formControlName`.
 */
@Component({
    selector: 'upp-er-input',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./er/upp-input.html',
    styleUrls: [ 
        './er/upp-input.scss'
    ]
})
export class UppErInputComponent implements OnChanges {
    /** The formGroup provided to the related upp-input. */
    @Input() form: FormGroup | null = null;
    /** The name of the form control to track errors for. */
    @Input() name: string | null = null;
    /** A mapping of error keys to their respective error messages. */
    @Input() errornfo: ErrorInfo | null = null
    /** Whether the error messages should be centered. */
    @Input() centered = false;
    /** Forces OnChanges to be called on value change. */
    @Input() value: string | null = null;

    /** The list of active error keys. */
    public errors: string[] = []
    
    /** The form control associated with this component. */
    public control: AbstractControl | null = null;

    /**
     * Constructor for dependency injection.
     */    
    constructor(){
        // nothing to do
    }

    /**
     * Lifecycle hook. Updates the list of errors when inputs change.
     */    
    ngOnChanges() {
        if (this.form && this.name){
            this.control = this.form.get(this.name);
        }

        if (this.control){
            this.errors = [];
            if (this.errornfo && this.control.errors){
                this.errors = Object.keys(this.control.errors).filter(key => Object.prototype.hasOwnProperty.call(this.errornfo, key))
            }    
        }
    }
}

/**********************************/
/* UPP-INPUT COMPONENT            */
/**********************************/

/**
 * A reusable input component that supports Angular Forms integration,
 * custom validation, and advanced UI features like kiosk mode.
 *
 * @implements {ControlValueAccessor}
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
    selector: 'upp-input',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl:'./upp-input.html',
    styleUrls: [ 
        './upp-input.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppInputComponent),
            multi: true
        }            
    ]    
})
export class UppInputComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    /** Placeholder text for the input. */
    @Input() placeholder = '';
    /** The name of the form control this input is bound to. */
    @Input() formControlName = '';
    /** Indicates if the input should be readonly. */
    @Input() readonly = false;
    /** Indicates if the input should be disabled. */
    @Input() disabled = false;
    /** The type of the input field (e.g., `text`, `number`). */
    @Input() type = '';
    /** Enables od disabled the kiosk input */
    @Input() kiosk = true;
    /** A title to display above the input. */
    @Input() title = '';
    /**
     * Header (in-field label) rendering: `'title'` shows the internal floating
     * title label; `'none'` hides it when the host already provides an external
     * label (e.g. dimension cells in the floor editor grids).
     */
    @Input() header: 'title' | 'none' = 'title';
    /** Configures autocomplete behavior for the input. */
    @Input() autocomplete = 'off';    
    /** A mapping of error keys to their respective error messages. */
    @Input() errornfo: ErrorInfo = {}

    /** Host class for the header-less variant (`header === 'none'`). */
    @HostBinding('class.--no-title') get _noTitle(): boolean {
        return this.header === 'none';
    }

    /** Emits the current value when it changes. */
    @Output() Changed = new EventEmitter<string | null>(); 
    /** Emits an event when the input gains focus. */
    @Output() Focus = new EventEmitter<void>(); 

    @ViewChild('input', {static: false}) _input: any;   
    
    /** optional FromGroup */
    public formGroup: FormGroup | null = null
    public formControl: AbstractControl | null = null;

    private _innerValue: string | null | undefined = undefined;  

    /* eslint-disable @typescript-eslint/no-unused-vars */
    /** Callback to notify Angular Forms when the value changes. */
    private _onChangeCallback = (v: any) => {
        // nothing to do (required by angular)
    }
    /** Callback to notify Angular Forms when the input is touched. */
    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    }
    /* eslint-enable @typescript-eslint/no-unused-vars */

    private _showtitle = false;

    /**
     * Determines whether the input's title should be displayed.
     * @returns {boolean} True if the title should be displayed; otherwise, false.
     */    
    get showTitle(): boolean {
        return this._showtitle;
    }

    private _itemtitle: string | null = null;

    /**
     * Returns the processed title value.
     * @returns {string} The input's title.
     */    
    get viewTitle(): string {
        return this._itemtitle || '';
    }

    /**
     * Constructor for dependency injection.
     * @param controlContainer Provides access to the parent form group.
     * @param change A service to trigger manual change detection.
     */    
    constructor(@Optional() private controlContainer: ControlContainer, private change: ChangeDetectorRef) {
        // nothing to do
    }

    private _form_subscription: Subscription | null = null;

    /**
     * Lifecycle hook that initializes the component.
     * Sets up form control integration and initializes the value.
     */    
    ngOnInit() {
        this._innerValue = this.value;
        
        if (this.controlContainer && this.controlContainer instanceof FormGroupDirective){
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (this.formControl){
            this._innerValue = stringifyFormBoundValue(this.formControl.value);

            this._form_subscription = this.formControl.valueChanges.subscribe(
            () => {
                this.value = stringifyFormBoundValue(this.formControl?.value);
            });
        }

        this.onChange();
    }

    /**
     * Lifecycle hook that detects changes to the input's properties.
     */    
    ngOnChanges() {
        this.onChange();
    }
    
    /**
     * Lifecycle hook that cleans up resources on component destruction.
     * Unsubscribes from form control value changes.
     */
    ngOnDestroy() {
        if (this._form_subscription){
            this._form_subscription.unsubscribe();
            this._form_subscription = null;
        }        
    }

    /**
     * Handles key press events and delegates them to the input element.
     * @param key The key that was pressed.
     */    
    OnKeyPress(key: string) : void {
        if (this._input){
            this._input.OnKeyPress(key);
        }
    }

    /**
     * Sets focus on the input element.
     */    
    SetFocus(): void {
        if (this._input){
            this._input.SetFocus();
        }
    }

    /**
     * Handles value changes and updates the title visibility.
     * Emits a value change event.
     */
    onChange(): void {
        this._itemtitle = (this.title) ? this.title : this.placeholder;
        this._showtitle = (this._innerValue !== null) && (this._innerValue !== '');
        if (this._innerValue != undefined){
            this.Changed.emit(this._innerValue);
        }

        this.change.markForCheck();
    }

    /**
     * Emits a focus event when the input gains focus.
     */    
    onFocus(): void {
        this.Focus.emit();
    }

    /********************************************/
    /* disable control                          */
    /********************************************/

    /**
     * Determines whether the input is disabled.
     * @returns {boolean} True if the input is disabled; otherwise, false.
     */    
    get _disabled(): boolean {
        // if directly disabled, return true
        if (this.disabled){
            return true;
        }

        // check if the the form control is disabled
        const _formcontrol = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (_formcontrol){
            return _formcontrol.disabled;
        }

        // not disabled
        return false;
    }

    /**
     * Determines whether the input must display error information
     * @returns {boolean} True if the input has errors; otherwise, false.
     */      
    get errors(): boolean {
        if (this.formControl){
            return (this.formControl.dirty || this.formControl.touched) && (this.formControl.errors != null);
        }
        return false;
    }

    /********************************************/
    /* get/set accesors                         */
    /********************************************/
    
    @Input()

    /**
     * Gets the current value of the input.
     * @returns {string | null} The current value of the input.
     */    
    get value(): string | null {
        if (this._innerValue === null || this._innerValue === undefined || this._innerValue === '') {
            return null;
        }
        return this._innerValue;
    }

    /**
     * Sets a new value for the input.
     * Updates the FormControl (CVA) before emitting `Changed`, so listeners that read
     * the control see the latest keystroke (same order as `UppKbInputComponent`).
     * @param {string | null} v - The new value to set.
     */    
    set value(v: string | null) {
        if (v != this._innerValue) {
            this._innerValue = v;

            this._onChangeCallback(v);
            this._onTouchCallback();

            this.onChange();
        }
    }    
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    /**
     * Writes a value to the input. Required by `ControlValueAccessor`.
     * @param value - The value to write (often a string; numeric controls may pass a number).
     */    
    writeValue(value: unknown) {
        const asStr = stringifyFormBoundValue(value);
        if (asStr !== this._innerValue) {
            this.value = asStr;
        }
    }

    /**
     * Registers a callback function to be called when the input value changes.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */    
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Registers a callback function to be called when the input is touched.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */    
    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    }    
}